import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MenuVisibilityService } from './menu-visibility.service';
import { Subscription, interval } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ChatService, NotificationsService, PurchasingMailService, SupportMailService } from '../stokker/api';
import { MENU_MAIL, MENU_SETTINGS, userHasMenu } from '../auth/menu-access';

interface NavChild {
  label: string;
  link: string;
  exact: boolean;
  /** olvasatlan-jelvény (Levelezés / beszerzési levelezés) */
  badge?: 'mailUnread' | 'purchaseMailUnread';
  /** külön menü-jogosultsághoz kötött almenü */
  menu?: string;
}

interface NavItem {
  label: string;
  link: string;
  exact: boolean;
  icon: SafeHtml;
  /** melyik menü-jogosultság dönt a láthatóságról (alapból a címke) */
  menu?: string;
  /** almenü — lenyílik, ha a szülő vagy bármelyik almenü útvonala aktív */
  children?: NavChild[];
  /** csoport: nincs saját oldala / jogosultsága, az almenüi döntenek */
  group?: boolean;
}

export const SVG_OPEN = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">';
export const SVG_CLOSE = '</svg>';

/** Ikonok a design prototípus navIcon() definícióiból (23-logic.js) — 1:1 SVG. */
export const NAV_ICONS: { [label: string]: string } = {
  'Mai nap': '<path d="M4 11 L12 4 l8 7"></path><path d="M6 10 v9 h12 v-9"></path><path d="M10 19 v-5 h4 v5"></path>',
  'Chat': '<path d="M4 5.5 h16 a1.5 1.5 0 0 1 1.5 1.5 v9 a1.5 1.5 0 0 1 -1.5 1.5 H10 l-4.5 3.5 V17.5 H4 A1.5 1.5 0 0 1 2.5 16 V7 A1.5 1.5 0 0 1 4 5.5 Z"></path><line x1="7" y1="10" x2="17" y2="10"></line><line x1="7" y1="13.5" x2="13" y2="13.5"></line>',
  'Foxi': '<path d="M4 4 L9 8.5 h6 L20 4 v8 c0 4.5 -3.6 8 -8 8 s-8 -3.5 -8 -8 Z"></path><circle cx="9.3" cy="12" r="0.9" fill="currentColor"></circle><circle cx="14.7" cy="12" r="0.9" fill="currentColor"></circle><path d="M11 15.5 h2 l-1 1.2 Z"></path>',
  // gyárépület fűrészfogas tetővel (Farmy — gyártásmérnök)
  'Farmy': '<path d="M3 20 V10 l5 3 V10 l5 3 V7 l4 2 V4 h3 v16 Z"></path><path d="M7 16 h2"></path><path d="M11 16 h2"></path><path d="M15 16 h2"></path>',
  'Boris': '<rect x="4.5" y="5" width="15" height="14" rx="5"></rect><circle cx="9.3" cy="11.5" r="2.1"></circle><circle cx="14.7" cy="11.5" r="2.1"></circle><path d="M11.4 11.5 h1.2"></path><path d="M12 2.5 v2.5"></path><path d="M9.5 16 h5"></path>',
  // raktárépület polcokkal
  'Raktár': '<path d="M3 9 L12 4 L21 9 V20 H3 Z"></path><rect x="7" y="12" width="10" height="8"></rect><line x1="7" y1="16" x2="17" y2="16"></line>',
  'Szállítás': '<rect x="2" y="7" width="12" height="9" rx="1.5"></rect><path d="M14 10 h4 l3 3 v3 h-7"></path><circle cx="7" cy="18.5" r="1.7"></circle><circle cx="17" cy="18.5" r="1.7"></circle>',
  // csillogás (MI-kollégák)
  'Agentek': '<path d="M12 3 l1.8 5.2 L19 10 l-5.2 1.8 L12 17 l-1.8 -5.2 L5 10 l5.2 -1.8 Z"></path><path d="M18.5 15 l0.8 2.2 L21.5 18 l-2.2 0.8 L18.5 21 l-0.8 -2.2 L15.5 18 l2.2 -0.8 Z"></path>',
  'Komponensek': '<circle cx="12" cy="7" r="2.7"></circle><circle cx="7" cy="16.5" r="2.7"></circle><circle cx="17" cy="16.5" r="2.7"></circle>',
  'Készlet': '<rect x="4" y="4" width="7" height="7" rx="1.5"></rect><rect x="13" y="4" width="7" height="7" rx="1.5"></rect><rect x="4" y="13" width="7" height="7" rx="1.5"></rect><rect x="13" y="13" width="7" height="7" rx="1.5"></rect>',
  'BGFox termékek': '<path d="M4 7.5 L12 3.5 L20 7.5 V16.5 L12 20.5 L4 16.5 Z"></path><polyline points="4,7.5 12,11.5 20,7.5"></polyline><line x1="12" y1="11.5" x2="12" y2="20.5"></line>',
  'Raktár szabály': '<path d="M12 3 L20 6.5 V12 C20 16.6 16.6 19.8 12 21 C7.4 19.8 4 16.6 4 12 V6.5 Z"></path><polyline points="9,12 11.3,14.3 15.5,9.3"></polyline>',
  'Gyártás': '<path d="M7 9 V4 h10 v5"></path><rect x="4" y="9" width="16" height="8" rx="2"></rect><path d="M7 14 h10 v6 H7 Z"></path>',
  'Beszerzés': '<path d="M3 4 h2.3 l2 10.5 h9.6"></path><path d="M6.4 7.5 h13.4 l-1.6 6.2 H7.6"></path><circle cx="9" cy="19" r="1.5"></circle><circle cx="17" cy="19" r="1.5"></circle>',
  'Anyagok': '<path d="M12 3 L21 8 L12 13 L3 8 Z"></path><polyline points="3,12 12,17 21,12"></polyline><polyline points="3,16 12,21 21,16"></polyline>',
  'Rendelések': '<polyline points="3,4 6,4 8.4,14.5 18.6,14.5 20.6,7.5 7.2,7.5"></polyline><circle cx="9.4" cy="19" r="1.5"></circle><circle cx="17.4" cy="19" r="1.5"></circle>',
  'Számlázás': '<rect x="5" y="3" width="14" height="18" rx="2"></rect><line x1="9" y1="8" x2="15" y2="8"></line><line x1="9" y1="12" x2="15" y2="12"></line><line x1="9" y1="16" x2="12" y2="16"></line>',
  'Felhasználók': '<circle cx="9" cy="8.5" r="3"></circle><path d="M3.5 19 a5.5 5.5 0 0 1 11 0"></path><circle cx="17" cy="8" r="2.4"></circle><path d="M15 14.5 a5 5 0 0 1 5.5 4.5"></path>',
  'Szállítók': '<rect x="2" y="7" width="12" height="9" rx="1.5"></rect><path d="M14 10 h4 l3 3 v3 h-7"></path><circle cx="7" cy="18.5" r="1.7"></circle><circle cx="17" cy="18.5" r="1.7"></circle>',
  'Futár rendelés': '<path d="M21 3 L10.5 13.5"></path><path d="M21 3 L14.5 21 L10.5 13.5 L3 9.5 Z"></path>',
  'Tracking': '<rect x="2" y="7" width="12" height="9" rx="1.5"></rect><path d="M14 10 h4 l3 3 v3 h-7"></path><circle cx="7" cy="18.5" r="1.7"></circle><circle cx="17" cy="18.5" r="1.7"></circle>',
  'Support': '<circle cx="12" cy="12" r="9"></circle><circle cx="12" cy="12" r="3.6"></circle><line x1="5.8" y1="5.8" x2="9.5" y2="9.5"></line><line x1="18.2" y1="5.8" x2="14.5" y2="9.5"></line><line x1="5.8" y1="18.2" x2="9.5" y2="14.5"></line><line x1="18.2" y1="18.2" x2="14.5" y2="14.5"></line>',
  'Munkaidő': '<circle cx="12" cy="12" r="9"></circle><polyline points="12,7 12,12 15.5,14"></polyline>',
  'Munkaidő admin': '<circle cx="9" cy="12" r="6.5"></circle><polyline points="9,8.5 9,12 11.5,13.5"></polyline><path d="M18.5 5.5 v3 M17 7 h3 M18.5 15.5 v3 M17 17 h3"></path>',
  'Etsy shopok': '<path d="M4 9 L5.5 4 h13 L20 9"></path><path d="M4 9 h16 v1.5 a2.7 2.7 0 0 1 -5.3 0 a2.7 2.7 0 0 1 -5.4 0 a2.7 2.7 0 0 1 -5.3 0 Z"></path><path d="M5.5 13 V20 h13 V13"></path>',
  'Riportok': '<rect x="4.5" y="12" width="3.4" height="7.5" rx="1"></rect><rect x="10.3" y="6.5" width="3.4" height="13" rx="1"></rect><rect x="16.1" y="9.5" width="3.4" height="10" rx="1"></rect>',
};

/** Nav sorrend + route-térkép. A csoportok (group: true) több oldalt fognak össze: a csoport akkor látszik, ha
 *  legalább egy almenüje látható (almenünként a saját jogosultsága dönt), a fejlécre kattintva az első látható
 *  almenü nyílik meg, és lenyitva marad, amíg valamelyik oldalán vagy. */
const NAV_ROUTES: { label: string; link: string; exact?: boolean; menu?: string; group?: boolean; children?: NavChild[] }[] = [
  { label: 'Mai nap', link: '/mai-nap' },
  { label: 'Rendelések', link: '/orders' },
  { label: 'Chat', link: '/chat' },
  {
    label: 'Raktár',
    link: '/inventory',
    group: true,
    children: [
      { label: 'Készlet', link: '/inventory', exact: false, menu: 'Készlet' },
      { label: 'Komponensek', link: '/components', exact: false, menu: 'Komponensek' },
      // BGFox: csökkentett üzemmód, csak megtekintés (nincs készletvezetés)
      { label: 'BGFox termékek', link: '/bgfox-termekek', exact: false, menu: 'BGFox termékek' },
      { label: 'Raktár szabály', link: '/raktar-szabaly', exact: false, menu: 'Raktár szabály' },
    ],
  },
  { label: 'Gyártás', link: '/gyartas' },
  {
    label: 'Beszerzés',
    link: '/beszerzes',
    group: true,
    children: [
      { label: 'Rendelési lista', link: '/beszerzes', exact: true, menu: 'Beszerzés' },
      { label: 'Rendelések', link: '/beszerzes/rendelesek', exact: false, menu: 'Beszerzés' },
      { label: 'Levelezés', link: '/beszerzes/levelezes', exact: false, badge: 'purchaseMailUnread', menu: 'Beszerzés' },
      { label: 'Beszállítók', link: '/beszerzes/beszallitok', exact: false, menu: 'Beszerzés' },
      { label: 'Készlethiány', link: '/beszerzes/keszlet', exact: false, menu: 'Beszerzés' },
      { label: 'Anyagok', link: '/anyagok', exact: false, menu: 'Anyagok' },
    ],
  },
  {
    label: 'Szállítás',
    link: '/carriers',
    group: true,
    children: [
      { label: 'Szállítók', link: '/carriers', exact: false, menu: 'Szállítók' },
      { label: 'Futár rendelés', link: '/futar-rendeles', exact: false, menu: 'Futár rendelés' },
    ],
  },
  { label: 'Tracking', link: '/tracking' },
  {
    label: 'Support',
    link: '/support',
    children: [
      { label: 'Tickets', link: '/support', exact: true },
      { label: 'Levelezés', link: '/support/levelezes', exact: false, badge: 'mailUnread', menu: 'Levelezés' },
    ],
  },
  { label: 'Számlázás', link: '/szamlazas' },
  {
    label: 'Munkaidő',
    link: '/munkaido',
    group: true,
    children: [
      { label: 'Nyilvántartás', link: '/munkaido', exact: true, menu: 'Munkaidő' },
      { label: 'Munkaidő admin', link: '/munkaido/admin', exact: false, menu: 'Munkaidő admin' },
    ],
  },
  { label: 'Riportok', link: '/riportok' },
  { label: 'Etsy shopok', link: '/merchants/etsy' },
  { label: 'Felhasználók', link: '/users' },
  {
    label: 'Agentek',
    link: '/foxi',
    group: true,
    children: [
      // saját „Foxi” / „Boris” / „Farmy” jog, kulcs nélkül csak adminnak
      { label: 'Foxi', link: '/foxi', exact: false, menu: 'Foxi' },
      { label: 'Boris', link: '/boris', exact: false, menu: 'Boris' },
      { label: 'Farmy', link: '/farmy', exact: false, menu: 'Farmy' },
    ],
  },
];

@Component({
  selector: 'app-left-menu',
  templateUrl: './left-menu.component.html',
  styleUrls: ['./left-menu.component.scss']
})
export class LeftMenuComponent implements OnInit, OnDestroy {

  navItems: NavItem[] = [];
  settingsIcon: SafeHtml;
  logoutIcon: SafeHtml;
  /** almenü: pontos útvonal-egyezés, de a query paraméterek (pl. ?ticket=12) nem számítanak */
  readonly exactPathOnly = { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } as const;
  /** olvasatlan support-beszélgetések száma (a Levelezés jelvénye) */
  mailUnread = 0;
  /** olvasatlan beszerzési levelek (beszerzes@) */
  purchaseMailUnread = 0;
  /** olvasatlan értesítések (harang) */
  notifUnread = 0;
  /** olvasatlan chat-üzenetek */
  chatUnread = 0;
  private readonly subs: Subscription[] = [];

  constructor(
    private menuVisibilityService: MenuVisibilityService,
    private router: Router,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private supportMail: SupportMailService,
    private purchasingMail: PurchasingMailService,
    private notifications: NotificationsService,
    private chat: ChatService,
  ) {
    this.navItems = NAV_ROUTES.map(r => ({
      label: r.label,
      link: r.link,
      exact: !!r.exact,
      menu: r.menu,
      children: r.children,
      group: !!r.group,
      icon: this.sanitizer.bypassSecurityTrustHtml(SVG_OPEN + (NAV_ICONS[r.label] || '') + SVG_CLOSE),
    }));
    this.settingsIcon = this.sanitizer.bypassSecurityTrustHtml(
      SVG_OPEN
      + '<line x1="4" y1="8" x2="20" y2="8"></line><circle cx="9" cy="8" r="2.4" fill="#17191C"></circle>'
      + '<line x1="4" y1="16" x2="20" y2="16"></line><circle cx="15" cy="16" r="2.4" fill="#17191C"></circle>'
      + SVG_CLOSE);
    this.logoutIcon = this.sanitizer.bypassSecurityTrustHtml(
      SVG_OPEN
      + '<path d="M13 4 H6 a2 2 0 0 0 -2 2 v12 a2 2 0 0 0 2 2 h7"></path>'
      + '<line x1="10" y1="12" x2="20" y2="12"></line><polyline points="16,8 20,12 16,16"></polyline>'
      + SVG_CLOSE);
  }

  ngOnInit() {
    this.subs.push(
      this.router.events.subscribe((event: Event) => {
        if (event instanceof NavigationEnd) {
          if (this.menuVisibilityService.mobileMenuIsOpen) {
            this.menuVisibilityService.mobileMenuIsOpen = false;
          }
        }
      }),
      this.supportMail.unread$.subscribe((count) => (this.mailUnread = count)),
      this.purchasingMail.unread$.subscribe((count) => (this.purchaseMailUnread = count)),
      this.notifications.unread$.subscribe((count) => (this.notifUnread = count)),
      this.chat.unread$.subscribe((count) => (this.chatUnread = count)),
      // Az olvasatlan levelek száma két percenként frissül (ha a Support menü látszik).
      interval(120000).subscribe(() => this.refreshMailUnread()),
    );
    this.refreshMailUnread();
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  private refreshMailUnread() {
    if (userHasMenu(this.authService.user, MENU_MAIL)) {
      this.supportMail.refreshUnread();
    }
    if (userHasMenu(this.authService.user, 'Beszerzés')) {
      this.purchasingMail.refreshUnread();
    }
  }

  /** A szülő menüpont jelvénye (bezárt almenünél): a látható almenük jelvényeinek összege. */
  parentBadge(item: NavItem): number {
    return this.visibleChildren(item).reduce((sum, child) => sum + this.badgeOf(child), 0);
  }

  /** A Beállítások link csak annak látszik, akinek joga van hozzá (alapból az adminnak). */
  get canSeeSettings(): boolean {
    return userHasMenu(this.authService.user, MENU_SETTINGS);
  }

  visibleChildren(item: NavItem): NavChild[] {
    return (item.children || []).filter((child) => !child.menu || userHasMenu(this.authService.user, child.menu));
  }

  /** A menüpont célja: csoportnál az első látható almenü. */
  linkOf(item: NavItem): string {
    return item.group ? this.visibleChildren(item)[0]?.link || item.link : item.link;
  }

  /** Lenyitott-e: a saját útvonala vagy valamelyik almenüé aktív. */
  isOpen(item: NavItem): boolean {
    const active = (link: string, exact: boolean) =>
      this.router.isActive(link, { paths: exact ? 'exact' : 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' });
    return (!item.group && active(item.link, item.exact)) || this.visibleChildren(item).some((c) => active(c.link, c.exact));
  }

  get meName(): string {
    return this.authService.user?.full_name || this.authService.user?.username || '';
  }

  get meImage(): string | null {
    return this.authService.user?.image_file || null;
  }

  get meInitial(): string {
    return (this.meName.trim().charAt(0) || '?').toUpperCase();
  }

  get meColor(): string {
    const palette = ['#C77B9B', '#5E7FA8', '#6E9663', '#B9834A', '#9A6E9E', '#C9A45C'];
    let hash = 0;
    for (const ch of this.meName) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    return palette[hash % palette.length];
  }

  badgeOf(child: NavChild): number {
    return child.badge === 'mailUnread' ? this.mailUnread : child.badge === 'purchaseMailUnread' ? this.purchaseMailUnread : 0;
  }

  /** A user `menus` jogosultság-térképe szerint látható elemek.
   *  Hiányzó térkép vagy hiányzó kulcs = látható (a prototípus
   *  `menus[m] !== false` szemantikája). */
  get visibleNavItems(): NavItem[] {
    return this.navItems.filter((item) =>
      item.group ? this.visibleChildren(item).length > 0 : userHasMenu(this.authService.user, item.menu || item.label));
  }

  isMobileMenuOpen() {
    return this.menuVisibilityService.mobileMenuIsOpen;
  }

  closeMenu() {
    this.menuVisibilityService.mobileMenuIsOpen = false;
  }

  logout() {
    this.authService.logout();
  }

  getUserName() {
    return this.authService.user?.full_name;
  }
}
